// src/pages/ChatInfoPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avatar, Box, Button, Chip, CircularProgress, Divider,
  IconButton, InputBase, List, ListItem, ListItemAvatar,
  ListItemText, Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PersonRemove as PersonRemoveIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  DeleteSweep as DeleteSweepIcon,
  DeleteForever as DeleteForeverIcon,
} from '@mui/icons-material';
import {
  arrayRemove, arrayUnion, collection, deleteDoc, doc,
  getDoc, getDocs, updateDoc, writeBatch,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';

const PURPLE = '#7C3AED';
const RED = '#E53935';

const ROLE_LABELS = { admin: 'Admin', teacher: 'Prof', parent: 'Parent', student: 'Élève' };
const ROLE_COLORS = { admin: '#E53935', teacher: '#1E88E5', parent: '#43A047', student: '#FB8C00' };

const normalizeRole = (r = '') => {
  const v = r.toLowerCase();
  if (v.includes('admin')) return 'admin';
  if (v.includes('prof') || v.includes('teacher')) return 'teacher';
  if (v.includes('parent')) return 'parent';
  return 'student';
};

const RoleChip = ({ role }) => {
  const key = normalizeRole(role);
  return (
    <Box component="span" sx={{
      fontSize: 10, fontWeight: 700, px: 0.8, py: 0.2, borderRadius: 1,
      bgcolor: (ROLE_COLORS[key] || '#999') + '20',
      color: ROLE_COLORS[key] || '#999', ml: 0.5,
    }}>
      {ROLE_LABELS[key] || role}
    </Box>
  );
};

// ── Suppression par batch d'une sous-collection entière ─────────────────────
const deleteSubcollection = async (chatId, subName) => {
  const snap = await getDocs(collection(db, 'chats', chatId, subName));
  const docs = snap.docs;
  const CHUNK = 400;
  for (let i = 0; i < docs.length; i += CHUNK) {
    const batch = writeBatch(db);
    docs.slice(i, i + CHUNK).forEach(d => batch.delete(d.ref));
    await batch.commit();
  }
};

// ── Modal de confirmation générique ─────────────────────────────────────────
const ConfirmModal = ({ open, title, message, confirmLabel, color, loading, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.45)', zIndex: 1300,
      display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
      onClick={onCancel}>
      <Box sx={{ bgcolor: '#fff', borderRadius: 3, p: 3, width: '100%', maxWidth: 360 }}
        onClick={e => e.stopPropagation()}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {message}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={onCancel} disabled={loading} sx={{ color: 'text.secondary' }}>
            Annuler
          </Button>
          <Button onClick={onConfirm} disabled={loading} variant="contained"
            sx={{ bgcolor: color, '&:hover': { bgcolor: color } }}>
            {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : confirmLabel}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default function ChatInfoPage() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();
  const me = auth.currentUser;

  const [loading, setLoading] = useState(true);
  const [chat, setChat] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersById, setUsersById] = useState({});
  const [search, setSearch] = useState('');
  const [addList, setAddList] = useState([]);
  const [isStaff, setIsStaff] = useState(false);
  const [saving, setSaving] = useState(false);

  // Édition du nom
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Actions destructives
  const [confirmMode, setConfirmMode] = useState(null); // null | 'clear' | 'delete'
  const [actionLoading, setActionLoading] = useState(false);

  const load = useMemo(() => async () => {
    setLoading(true);
    try {
      const [chatDoc, usersSnap, adminDoc] = await Promise.all([
        getDoc(doc(db, 'chats', chatId)),
        getDocs(collection(db, 'users')),
        me ? getDoc(doc(db, 'admins', me.uid)) : Promise.resolve({ exists: () => false }),
      ]);

      if (chatDoc.exists()) {
        const data = { id: chatDoc.id, ...chatDoc.data() };
        setChat(data);
        setNameInput(data.name || '');
      }
      setIsStaff(adminDoc.exists());

      const u = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsers(u);
      const map = {};
      u.forEach(x => {
        // L'ID du document Firestore = UID Firebase Auth (priorité absolue)
        map[x.id] = x;
        // Aussi indexer par x.uid si différent (sécurité)
        if (x.uid && x.uid !== x.id) map[x.uid] = x;
      });
      setUsersById(map);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [chatId, me]);

  useEffect(() => { load(); }, [load]);

  const removeParticipant = async (uid) => {
    if (!isStaff) return;
    try {
      await updateDoc(doc(db, 'chats', chatId), {
        participants: arrayRemove(uid),
      });
      await load();
    } catch (e) { console.error(e); }
  };

  const addParticipants = async () => {
    if (!isStaff || addList.length === 0) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'chats', chatId), {
        participants: arrayUnion(...addList),
      });
      setAddList([]);
      await load();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  // ── Modifier le nom du chat ────────────────────────────────────────────
  const startEditName = () => {
    setNameInput(chat?.name || '');
    setEditingName(true);
  };

  const cancelEditName = () => {
    setNameInput(chat?.name || '');
    setEditingName(false);
  };

  const saveChatName = async () => {
    if (!isStaff) return;
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === chat?.name) { setEditingName(false); return; }
    setSavingName(true);
    try {
      await updateDoc(doc(db, 'chats', chatId), { name: trimmed });
      setChat(prev => ({ ...prev, name: trimmed }));
      setEditingName(false);
    } catch (e) {
      console.error(e);
    } finally {
      setSavingName(false);
    }
  };

  // ── Effacer le contenu du chat (garde le chat, supprime les messages) ───
  const clearChatContent = async () => {
    if (!isStaff) return;
    setActionLoading(true);
    try {
      await deleteSubcollection(chatId, 'messages');
      await updateDoc(doc(db, 'chats', chatId), {
        lastMessageText: '',
        lastMessageSenderName: '',
        lastMessageSender: null,
      });
      setConfirmMode(null);
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  // ── Supprimer le chat entièrement ────────────────────────────────────────
  const deleteChat = async () => {
    if (!isStaff) return;
    setActionLoading(true);
    try {
      await deleteSubcollection(chatId, 'messages');
      await deleteSubcollection(chatId, 'members');
      await deleteDoc(doc(db, 'chats', chatId));
      navigate('/chats');
    } catch (e) {
      console.error(e);
      setActionLoading(false);
    }
  };

  if (loading || !chat) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress sx={{ color: PURPLE }} />
      </Box>
    );
  }

  // Grouper les participants par rôle normalisé
  const grouped = { admin: [], teacher: [], student: [], parent: [], other: [] };
  (chat.participants || []).forEach(uid => {
    const u = usersById[uid];
    const key = normalizeRole(u?.role || '');
    if (grouped[key]) grouped[key].push(uid);
    else grouped.other.push(uid);
  });

  const filteredForAdd = users.filter(u => {
    const q = search.trim().toLowerCase();
    const uid = u.id; // toujours l'ID Firestore
    const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ').toLowerCase();
    const hit = !q ||
      fullName.includes(q) ||
      (u.displayName || '').toLowerCase().includes(q) ||
      (u.pseudo || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q);
    const already = (chat.participants || []).includes(uid);
    return hit && !already;
  });

  // Description du type de chat
  const chatDescription = chat.type === 'course'
    ? `Chat de cours • ${
        chat.audience === 'students' ? 'Élèves' :
        chat.audience === 'parents' ? 'Parents' : 'Parents + Élèves'
      }`
    : chat.type === 'direct' ? 'Chat direct' : 'Chat de groupe';

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', pb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5,
        borderBottom: '1px solid #eee', position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 10 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ ml: 1, fontWeight: 700 }}>
          Infos du chat
        </Typography>
      </Box>

      <Box sx={{ px: 3, pt: 3 }}>
        {/* Nom + type */}
        {editingName ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <InputBase autoFocus value={nameInput} onChange={e => setNameInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') saveChatName();
                if (e.key === 'Escape') cancelEditName();
              }}
              sx={{ bgcolor: '#f5f5f5', borderRadius: 2, px: 2, py: 0.6, flex: 1,
                fontSize: 20, fontWeight: 700 }} />
            <IconButton size="small" onClick={saveChatName} disabled={savingName}
              sx={{ color: PURPLE }}>
              {savingName ? <CircularProgress size={18} /> : <CheckIcon />}
            </IconButton>
            <IconButton size="small" onClick={cancelEditName} disabled={savingName}>
              <CloseIcon />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 0 }}>
              {chat.name || 'Chat'}
            </Typography>
            {isStaff && (
              <IconButton size="small" onClick={startEditName} sx={{ color: 'text.secondary' }}>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}

        <Chip label={chatDescription} size="small"
          sx={{ bgcolor: PURPLE + '15', color: PURPLE, fontWeight: 600, mb: 2, mt: editingName ? 1.5 : 1 }} />

        <Typography variant="caption" color="text.secondary">
          {chat.participants?.length || 0} participant{(chat.participants?.length || 0) > 1 ? 's' : ''}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Participants par rôle */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
          Participants
        </Typography>

        {['admin', 'teacher', 'student', 'parent', 'other'].map(role => {
          const list = grouped[role];
          if (!list?.length) return null;
          return (
            <Box key={role} sx={{ mb: 2 }}>
              <Chip label={ROLE_LABELS[role] || role.toUpperCase()} size="small"
                sx={{ mb: 1, bgcolor: (ROLE_COLORS[role] || '#999') + '15',
                  color: ROLE_COLORS[role] || '#999', fontWeight: 700 }} />
              <List dense disablePadding>
                {list.map(uid => {
                  const u = usersById[uid];
                  const fullName = u ? [u.firstName, u.lastName].filter(Boolean).join(' ') : '';
                  const label = fullName || u?.displayName || u?.pseudo ||
                    u?.email?.split('@')[0] || uid.slice(0, 6);
                  const isMe = uid === me?.uid;
                  return (
                    <ListItem key={uid}
                      sx={{ borderRadius: 2, '&:hover': { bgcolor: '#f9f9f9' } }}
                      secondaryAction={
                        isStaff && !isMe ? (
                          <IconButton edge="end" size="small"
                            onClick={() => removeParticipant(uid)}
                            sx={{ color: 'error.main' }}>
                            <PersonRemoveIcon fontSize="small" />
                          </IconButton>
                        ) : null
                      }>
                      <ListItemAvatar>
                        <Avatar sx={{ width: 36, height: 36,
                          bgcolor: (ROLE_COLORS[role] || '#999') + '20',
                          color: ROLE_COLORS[role] || '#999', fontSize: 14 }}>
                          {label[0]?.toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {label}
                            {isMe && (
                              <Box component="span" sx={{ fontSize: 10, color: 'text.secondary', ml: 0.5 }}>
                                (moi)
                              </Box>
                            )}
                          </Box>
                        }
                        secondary={u?.email}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          );
        })}

        {/* Section ajout — staff uniquement */}
        {isStaff && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              Ajouter des participants
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <InputBase fullWidth placeholder="Rechercher par nom / email…"
                value={search} onChange={e => setSearch(e.target.value)}
                sx={{ bgcolor: '#f5f5f5', borderRadius: 2, px: 2, py: 0.8, flex: 1 }} />
              <Box onClick={addParticipants}
                sx={{ bgcolor: addList.length > 0 ? PURPLE : '#ccc', color: '#fff',
                  borderRadius: 2, px: 2, py: 1, cursor: addList.length > 0 ? 'pointer' : 'default',
                  fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center',
                  gap: 0.5, whiteSpace: 'nowrap' }}>
                {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : `Ajouter${addList.length > 0 ? ` (${addList.length})` : ''}`}
              </Box>
            </Box>

            {/* Chips sélectionnés */}
            {addList.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {addList.map(uid => {
                  const u = usersById[uid];
                  const chipName = u ? ([u.firstName, u.lastName].filter(Boolean).join(' ') ||
                    u.displayName || u.pseudo || u.email?.split('@')[0]) : uid.slice(0, 6);
                  const name = chipName || uid.slice(0, 6);
                  return (
                    <Chip key={uid} label={name} size="small"
                      onDelete={() => setAddList(prev => prev.filter(x => x !== uid))}
                      sx={{ bgcolor: PURPLE + '15', color: PURPLE }} />
                  );
                })}
              </Box>
            )}

            <Box sx={{ maxHeight: 260, overflow: 'auto', border: '1px solid #eee', borderRadius: 2 }}>
              <List dense>
                {filteredForAdd.slice(0, 200).map(u => {
                  const uid = u.uid || u.id;
                  const fullName2 = [u.firstName, u.lastName].filter(Boolean).join(' ');
                  const label = fullName2 || u.displayName || u.pseudo ||
                    u.email?.split('@')[0] || uid.slice(0, 6);
                  const sel = addList.includes(uid);
                  const roleKey = normalizeRole(u.role || '');
                  return (
                    <ListItem key={uid} button onClick={() =>
                      setAddList(prev => sel ? prev.filter(x => x !== uid) : [...prev, uid])}
                      sx={{ '&:hover': { bgcolor: '#f9f5ff' },
                        bgcolor: sel ? PURPLE + '08' : 'transparent' }}>
                      <ListItemAvatar>
                        <Avatar sx={{ width: 32, height: 32,
                          bgcolor: (ROLE_COLORS[roleKey] || '#999') + '20',
                          color: ROLE_COLORS[roleKey] || '#999', fontSize: 12 }}>
                          {label[0]?.toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {label}
                            <RoleChip role={u.role || ''} />
                          </Box>
                        }
                        secondary={u.email}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      {sel && <CheckIcon sx={{ color: PURPLE, fontSize: 18, flexShrink: 0 }} />}
                    </ListItem>
                  );
                })}
                {filteredForAdd.length === 0 && (
                  <ListItem>
                    <ListItemText secondary="Aucun utilisateur à ajouter" />
                  </ListItem>
                )}
              </List>
            </Box>
          </>
        )}

        {/* Zone dangereuse — staff uniquement */}
        {isStaff && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5, color: RED }}>
              Zone sensible
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                startIcon={<DeleteSweepIcon />}
                onClick={() => setConfirmMode('clear')}
                sx={{ justifyContent: 'flex-start', color: '#B45309', bgcolor: '#FEF3C7',
                  '&:hover': { bgcolor: '#FDE68A' }, borderRadius: 2, px: 2, py: 1, textTransform: 'none',
                  fontWeight: 600 }}>
                Effacer le contenu du chat
              </Button>
              <Button
                startIcon={<DeleteForeverIcon />}
                onClick={() => setConfirmMode('delete')}
                sx={{ justifyContent: 'flex-start', color: RED, bgcolor: RED + '15',
                  '&:hover': { bgcolor: RED + '25' }, borderRadius: 2, px: 2, py: 1, textTransform: 'none',
                  fontWeight: 600 }}>
                Supprimer ce chat
              </Button>
            </Box>
          </>
        )}
      </Box>

      {/* Confirmation : effacer le contenu */}
      <ConfirmModal
        open={confirmMode === 'clear'}
        title="Effacer le contenu du chat ?"
        message="Tous les messages de cette conversation seront supprimés définitivement. Le chat et ses participants seront conservés."
        confirmLabel="Effacer"
        color="#B45309"
        loading={actionLoading}
        onConfirm={clearChatContent}
        onCancel={() => setConfirmMode(null)}
      />

      {/* Confirmation : supprimer le chat */}
      <ConfirmModal
        open={confirmMode === 'delete'}
        title="Supprimer ce chat ?"
        message="Cette action est irréversible : le chat, tous ses messages et ses membres seront définitivement supprimés."
        confirmLabel="Supprimer"
        color={RED}
        loading={actionLoading}
        onConfirm={deleteChat}
        onCancel={() => setConfirmMode(null)}
      />
    </Box>
  );
}